import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import images from "assets/img";
import useSWR from "swr";
import { Box, Button, Typography } from "@mui/material";
import { previewModal } from "store/slices/miscSlice";
import { useDispatch } from "react-redux";
import RewardRate from "./RewardRate";
import usePermissions from "hooks/usePermissions";
import { formatAmount } from "Helper";
import { BsChevronRight } from "react-icons/bs";
import useBalances from "hooks/useBalances";

export default withTranslation()(function Cards({ t }) {
  const dispatch = useDispatch();
  const { data: stats } = useSWR("dashboard/overview");
  const ab = useBalances();

  const [highestRate, setHighestRate] = useState(0);

  useEffect(() => {
    // get Highest percentage
    const data = stats?.data?.rewards?.rates;
    if (data) {
      const list = data
        .map((x) => x.rate)
        .filter((x) => String(x).includes("%"))
        .map((x) => parseFloat(x));

      if (list && list.length > 0) {
        setHighestRate(Math.max(...list));
      }
    }
  }, [stats]);

  const { isPermitted } = usePermissions("view_total_transactions");

  return (
    <>
      <Box
        className="w-[100%] py-3 gap-[30px] flex"
        sx={{ flexDirection: { xs: "column", lg: "row" } }}
      >
        <Box
          className="justify-between items-center rounded-[16px] px-[24px] flex bg-white h-[106px]"
          width={{ xs: "100%", lg: "50%" }}
        >
          <div className="items-center">
            <Typography variant="h5" fontWeight="400" color="#05213D">
              {Number(highestRate)?.toFixed(2)}%
            </Typography>

            <Typography variant="body1" fontWeight={"700"} color={"#4F4F4F"}>
              Reward Rate
            </Typography>
            <Typography variant="body2" color={"#657585"}>
              Platform fee: {formatAmount(stats?.data?.platform_fee)}
            </Typography>
          </div>
          <Button
            variant="outlined"
            onClick={() => {
              if (stats) {
                dispatch(
                  previewModal({
                    title: "Reward rates",
                    content: () => (
                      <RewardRate data={stats?.data?.rewards?.rates} />
                    ),
                  })
                );
              }
            }}
            className="rounded-[16px] h-[56px] py-[20px] border-[#1976D2]"
            sx={{ fontWeight: "700", fontSize: "16px" }}
          >
            View all <BsChevronRight />
          </Button>
        </Box>
        <Box
          className="justify-between items-center rounded-[16px] px-[24px] flex bg-white h-[106px]"
          width={{ xs: "100%", lg: "50%" }}
        >
          <div className="items-center">
            <div className="text-dark text-[24px] font-[400] text-[#05213D]">
              {formatAmount(stats?.data?.credit_acccount_balance)}
            </div>
            <Typography variant="body1" fontWeight={"700"} color={"#4F4F4F"}>
              Credit Balance
            </Typography>
          </div>
          <Button
            variant="outlined"
            className="rounded-[16px] h-[56px] py-[20px] border-[#1976D2]"
            sx={{ fontWeight: "700", fontSize: "16px" }}
          >
            + Top Up
          </Button>
        </Box>
      </Box>

      <ul className="w-full grid md:grid-cols-3 md:gap-8 gap-4">
        <li className="bg-white p-5 rounded-2xl flex flex-col gap-[15px]">
          <div className="flex gap-[20px] justify-between items-center">
            <h5 className="font-small font-[700] text-[#657585]">
              {t("overview.credit")}
            </h5>
            <img src={images.Cash} alt="" className="w-[24px] h-[24px]" />
          </div>
          {isPermitted?.view_total_revenue ? (
            <h3 className="text-3xl  text-[#05213D]">{ab?.credit?.balance ?? "₦0"}</h3>
          ) : (
            "not permitted"
          )}
        </li>

        <li className="bg-white p-5 rounded-2xl flex flex-col gap-[15px]">
          <div className="flex gap-[20px] justify-between items-center">
            <h5 className="font-small font-[700] text-[#657585]">
              {t("overview.deposit")}
            </h5>
            <img src={images.Cash} alt="" className="w-[24px] h-[24px]" />
          </div>
          {isPermitted?.view_total_revenue ? (
            <h3 className="text-3xl  text-[#05213D]">
              {ab?.deposit?.balance ?? "₦0"}
            </h3>
          ) : (
            "not permitted"
          )}
        </li>

        <li className="bg-white p-5 rounded-2xl flex flex-col gap-[15px]">
          <div className="flex gap-[20px] justify-between items-center">
            <h5 className="font-small font-[700] text-[#657585]">
              {t("overview.cashflow")}
            </h5>
            <img src={images.Cash} alt="" className="w-[24px] h-[24px]" />
          </div>
          {isPermitted?.view_total_revenue ? (
            <h3 className="text-3xl  text-[#05213D]">
              {ab?.cash_flow?.balance ?? "₦0"}
            </h3>
          ) : (
            "not permitted"
          )}
        </li>
        <li className="bg-white p-5 rounded-2xl flex flex-col gap-[15px]">
          <div className="flex gap-[20px] justify-between items-center">
            <h5 className="font-small font-[700] text-[#657585]">
              {t("overview.revenue")}
            </h5>
            <img src={images.Cash} alt="" className="w-[24px] h-[24px]" />
          </div>
          {isPermitted?.view_total_revenue ? (
            <h3 className="text-3xl  text-[#05213D]">
              {stats?.data.total_revenue ?? "₦0"}
            </h3>
          ) : (
            "not permitted"
          )}
        </li>

        <li className="bg-white p-5 rounded-2xl flex flex-col gap-[15px]">
          <div className="flex gap-[20px] justify-between items-center">
            <h5 className="font-small font-[700] text-[#657585]">
              {t("overview.total_transaction")}
            </h5>
            <img
              src={images.Transaction}
              alt=""
              className="w-[24px] h-[24px]"
            />
          </div>
          {isPermitted?.view_total_transactions ? (
            <h3 className="text-3xl  text-[#05213D]">
              {stats?.data.total_transactions ?? "₦0"}
            </h3>
          ) : (
            <p>not permitted</p>
          )}
        </li>

        <li className="bg-white p-5 rounded-2xl flex flex-col gap-[15px]">
          <div className="flex gap-[20px] justify-between items-center">
            <h5 className="font-small font-[700] text-[#657585]">
              {t("overview.redemption_balance")}{" "}
            </h5>
            <img src={images.Wallet} alt="" className="w-[24px] h-[24px]" />
          </div>
          <h3 className="text-3xl  text-[#05213D]">
            {stats?.data.pending_settlement ?? "₦0"}
          </h3>
        </li>
      </ul>
    </>
  );
});
